import { useMemo, useState } from 'react';
import type { ReportSummary } from '../api/reports.js';
import type { DashboardSummary } from '../api/dashboards.js';
import { buildDefinitions } from './reports-instances/ConsumerReportsList.js';
import { EM_DASH, fmtDateTime } from '../lib/format.js';

/**
 * Category list tables for the embedded Insights viewer.
 *
 * Clicking a sidebar section header (Reports / Dashboards) lands here: a
 * Canopy-standard data table of that category's objects in the right pane
 * (design-standards/patterns/list-page.md — title row, toolbar with search,
 * zebra table, empty state; datetime.md for dates; `–` for empty values).
 * Clicking a row opens the same viewer a tree click opens.
 *
 * Reports rows are the consumer *definitions* (one per template, via the
 * same buildDefinitions the sidebar tree uses) so the table and the tree
 * always show the same objects with the same counts.
 */

export function ReportsCategoryList({
  reports,
  onOpen,
}: {
  reports: ReadonlyArray<ReportSummary>;
  onOpen(id: string): void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const shared = useMemo(() => reports.filter((r) => r.visibility !== 'personal'), [reports]);
  const mine = useMemo(() => reports.filter((r) => r.visibility === 'personal'), [reports]);
  const defs = useMemo(() => buildDefinitions(shared), [shared]);

  const q = query.trim().toLowerCase();
  const visibleDefs = q ? defs.filter((d) => d.label.toLowerCase().includes(q)) : defs;
  const visibleMine = q ? mine.filter((r) => r.name.toLowerCase().includes(q)) : mine;

  return (
    <ListShell
      title="Reports"
      subtitle="All reports available to you. Select one to view it."
      count={defs.length + mine.length}
      query={query}
      onQuery={setQuery}
      searchLabel="Search reports"
      empty={visibleDefs.length === 0 && visibleMine.length === 0}
      emptyText={
        defs.length + mine.length === 0
          ? 'No reports exist for your scope yet.'
          : 'None match this search.'
      }
    >
      <table className="data-table data-table--zebra">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th style={{ textAlign: 'right' }}>Locations</th>
          </tr>
        </thead>
        <tbody>
          {visibleDefs.map((d) => (
            <tr key={d.key} className="catalog-row" onClick={() => onOpen(d.representativeId)}>
              <td>
                <button
                  type="button"
                  className="catalog-link"
                  onClick={() => onOpen(d.representativeId)}
                >
                  {d.label}
                </button>
              </td>
              <td className="text-dim">Report</td>
              <td style={{ textAlign: 'right' }}>{d.count > 1 ? d.count : EM_DASH}</td>
            </tr>
          ))}
          {visibleMine.map((r) => (
            <tr key={r.id} className="catalog-row" onClick={() => onOpen(r.id)}>
              <td>
                <button type="button" className="catalog-link" onClick={() => onOpen(r.id)}>
                  {r.name}
                </button>
              </td>
              <td className="text-dim">My report</td>
              <td style={{ textAlign: 'right' }}>{EM_DASH}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ListShell>
  );
}

export function DashboardsCategoryList({
  dashboards,
  onOpen,
  title = 'Dashboards',
  subtitle = 'All dashboards available to you. Select one to view it.',
}: {
  dashboards: ReadonlyArray<DashboardSummary>;
  onOpen(id: string): void;
  /** Heading for the listing — the folder's name when scoped to one. */
  title?: string;
  subtitle?: string;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const visible = q
    ? dashboards.filter(
        (d) => d.name.toLowerCase().includes(q) || (d.description ?? '').toLowerCase().includes(q),
      )
    : dashboards;

  return (
    <ListShell
      title={title}
      subtitle={subtitle}
      count={dashboards.length}
      query={query}
      onQuery={setQuery}
      searchLabel="Search dashboards"
      empty={visible.length === 0}
      emptyText={
        dashboards.length === 0
          ? 'No dashboards exist for your scope yet.'
          : 'None match this search.'
      }
    >
      <table className="data-table data-table--zebra">
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((d) => (
            <tr key={d.id} className="catalog-row" onClick={() => onOpen(d.id)}>
              <td>
                <button type="button" className="catalog-link" onClick={() => onOpen(d.id)}>
                  {d.name}
                </button>
              </td>
              <td className="text-dim">{d.description?.trim() || EM_DASH}</td>
              <td className="text-dim">{fmtDateTime(d.updatedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ListShell>
  );
}

/** Shared list-page chrome: header row + search toolbar + table wrap. Reuses
 *  the Subscriptions page classes — both implement the same Canopy list-page
 *  pattern, so they stay visually identical by construction. */
function ListShell(props: {
  title: string;
  subtitle: string;
  count: number;
  query: string;
  onQuery(q: string): void;
  searchLabel: string;
  empty: boolean;
  emptyText: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="subs-manager">
      <header className="subs-header">
        <div>
          <h2 className="subs-title">{props.title}</h2>
          <p className="subs-subtitle">{props.subtitle}</p>
        </div>
        <div className="subs-header-badges">
          <span className="badge badge--neutral">{props.count} total</span>
        </div>
      </header>

      <div className="subs-toolbar">
        <div />
        <input
          type="search"
          className="subs-search"
          placeholder={`${props.searchLabel}…`}
          value={props.query}
          onChange={(e) => props.onQuery(e.target.value)}
          aria-label={props.searchLabel}
        />
      </div>

      {props.empty ? (
        <div className="empty-state" style={{ border: 'none' }}>
          <h3>Nothing here</h3>
          <p className="text-dim">{props.emptyText}</p>
        </div>
      ) : (
        <div className="subs-table-wrap">{props.children}</div>
      )}
    </div>
  );
}
